import { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { EditorProps } from 'react-draft-wysiwyg';
import { useEffectOnce } from 'react-use';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import dynamic from 'next/dynamic';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useUploadImages } from '../../../services';
import S from './TextEditor.module.scss';

// FIXME: 에디터 mount시 버그가 있습니다. 관련 이슈: https://github.com/jpuri/react-draft-wysiwyg/issues/951
const Editor = dynamic<EditorProps>(() => import('react-draft-wysiwyg').then(mod => mod.Editor as any), {
  ssr: false
}) as (props: EditorProps) => JSX.Element;

/**
 * draft.js Wysiwyg 텍스트 에디터 사용을 위한 컴포넌트
 */
const TextEditor = ({
  initialText,
  locale,
  onTextUpdate,
  placeholder
}: TextEditorProps, ref: Ref<TextEditorExposeType>) => {
  const [isMounted, setIsMounted] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  /**
   * 컴포넌트 외부에서 내부의 기능을 사용할 수 있는 함수를 정의하는 Handler
   */
  useImperativeHandle(ref, () => ({
    /**
     * 에디터의 텍스트를 외부에서 사용하기 위한 HTML String으로 변환하는 함수
     * @returns {string} 변환된 HTML String
     */
    getEditorText: () => {
      const rawContent = convertToRaw(editorState.getCurrentContent());
      return draftToHtml(rawContent);
    },
    setEditorText: async (text: string) => {
      const htmlToDraft = (await import('html-to-draftjs')).default;
      const desc = htmlToDraft(text);
      if (desc) {
        const contentState = ContentState.createFromBlockArray(desc.contentBlocks);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
      }
    }
  }));
  const imageUploadMutation = useUploadImages(0, {
    onSuccess: data => {
      if (data.status === 0) {
        return {
          data: {
            link: data.urls[0]
          }
        };
      }
    },
    onError: error => {
      console.error(error);
    }
  });

  /**
   * wysiwyg 내부 기능으로 에디터에서 이미지 업로드시 data url로 변환
   * @param file 업로드된 이미지
   */
  const handleWysiwygImageUpload = (file: File) => {
    return imageUploadMutation.mutate([file]);
  };

  /**
   * 텍스트 에디터에서 변경된 내용을 state에 반영하는 함수
   * @param editorState draft.js에서 전달하는 변경된 state
   */
  const handleEditorStateChange = (editorState: EditorState) => {
    setEditorState(editorState);
  };
  useEffectOnce(() => {
    (async () => {
      if (initialText) {
        const htmlToDraft = (await import('html-to-draftjs')).default;
        const desc = htmlToDraft(initialText);
        if (desc) {
          const contentState = ContentState.createFromBlockArray(desc.contentBlocks);
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      }
      setIsMounted(true);
    })();
  });
  if (!isMounted) {
    return <></>;
  }
  return <Editor editorState={editorState} onEditorStateChange={handleEditorStateChange} toolbarClassName={S.wysiwyg_toolbar} editorClassName={S.wysiwyg_editor} toolbar={{
    image: {
      uploadEnabled: false,
      uploadCallback: handleWysiwygImageUpload,
      previewImage: true
    },
    list: {
      options: ['unordered', 'ordered']
    }
  }} localization={{
    locale
  }} onChange={contentState => {
    onTextUpdate && onTextUpdate(draftToHtml(contentState));
  }} placeholder={placeholder} data-sentry-element="Editor" data-sentry-component="TextEditor" data-sentry-source-file="TextEditor.tsx" />;
};

/**
 * 외부에서 내부 함수를 호출할 수 있게 해주려면 forwardRef로 묶는 작업이 필요하다
 */
export default forwardRef(TextEditor);
type TextEditorProps = {
  initialText?: string;
  onTextUpdate?: (text: string) => void;
  disabled?: boolean;
  locale?: string;
  placeholder?: string;
};
export type TextEditorExposeType = {
  getEditorText: () => string;
  setEditorText: (text: string) => void;
};